import { useContext, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { getUser } from '@fs/zion-user'
import { PersonContext } from '../PersonContext'
import { deepCopy } from '../../../util/Utils'
import useNotifications from '../../../hooks/useNotifications'
import {
  addAdminLabel,
  deleteAdminLabel,
  editAdminLabel,
  getPersonLabelMetadata,
  getPersonLabels,
} from '../../../services/personService'
import { getSortedArr } from '../../../util/StringUtil'
import { PermissionsContext } from '../../app/PermissionsContext'

export default () => {}

export function usePersonLabelsController() {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const { currentPerson } = useContext(PersonContext)
  const unwantedLabels = ['INF_HOLOCAUST_APPROPRIATE']
  const { isAuthorized } = useContext(PermissionsContext)
  const user = getUser()
  const initialState = {
    constants: {
      labelTableHeaders: [
        { id: 'humanReadableText', sortable: true, label: t('label', 'Label') },
        {
          id: 'genealogicalDate',
          sortable: true,
          label: `${t('date', 'Date')}/${t('time', 'Time')}`,
        },
        { id: 'user', sortable: true, label: t('contributor', 'Contributor') },
        { id: 'reason', sortable: false, label: t('reason', 'Reason') },
        { id: 'blank', sortable: false, label: '' },
      ],
      changeTableHeaders: [
        {
          id: 'changeAction',
          sortable: true,
          label: t('action.action', 'Action'),
        },
        { id: 'humanReadableText', sortable: true, label: t('label', 'Label') },
        { id: 'user', sortable: true, label: t('user.label', 'User') },
        {
          id: 'timeStamp',
          sortable: true,
          label: `${t('date', 'Date')}/${t('time', 'Time')}`,
        },
        { id: 'reason', sortable: false, label: t('reason', 'Reason') },
      ],
    },
    adminLabels: { changes: [], labels: [] },
    labelsSuggestions: [],
    adding: false,
    addReason: '',
    deleteReason: '',
    labelReason: '',
    labelSuggestion: undefined,
  }

  const reducer = (_, currentState) => deepCopy(currentState)
  const [personLabelsState, setPersonLabelsState] = useReducer(reducer, initialState)

  const personLabelsActions = {
    setAdminLabels: (labels, changes) => {
      personLabelsState.adminLabels.labels = labels
      personLabelsState.adminLabels.changes = changes
      personLabelsActions.updatePersonLabelsState()
    },
    setLabelsSuggestions: (labelsSuggestions) => {
      const filteredSuggestions = labelsSuggestions.filter((label) => {
        // Filter out labels that the user is not authorized to update
        return isAuthorized(label.label.updatePermission)
      })
      personLabelsState.labelsSuggestions = filteredSuggestions
      personLabelsActions.updatePersonLabelsState()
    },
    setAdding: (adding) => {
      personLabelsState.adding = adding
      personLabelsActions.updatePersonLabelsState()
    },
    setAddReason: (addReason) => {
      personLabelsState.addReason = addReason
      personLabelsActions.updatePersonLabelsState()
    },
    setDeleteReason: (deleteReason) => {
      personLabelsState.deleteReason = deleteReason
      personLabelsActions.updatePersonLabelsState()
    },
    setLabelReason: (labelReason) => {
      personLabelsState.labelReason = labelReason
      personLabelsActions.updatePersonLabelsState()
    },
    setLabelSuggestion: (labelSuggestion) => {
      personLabelsState.labelSuggestion = labelSuggestion
      personLabelsActions.updatePersonLabelsState()
    },
    reset: () => {
      personLabelsState.adding = false
      personLabelsState.addReason = ''
      personLabelsState.deleteReason = ''
      personLabelsState.labelSuggestion = undefined
      personLabelsActions.updatePersonLabelsState()
    },
    getPersonLabelMetadata: async () => {
      return getPersonLabelMetadata()
        .then((data) => {
          if (data == null || data.length === 0) {
            personLabelsActions.setLabelsSuggestions([])
            return []
          }

          const labels = getSortedArr(data, 'humanReadableValueText', 'desc')
          const suggestions = labels
            .filter((label) => !unwantedLabels.includes(label.valueText))
            .map((label, index) => {
              return {
                key: index,
                primaryText: label.humanReadableValueText || label.valueText,
                label,
              }
            })
          personLabelsActions.setLabelsSuggestions(suggestions)
          return suggestions
        })
        .catch((err) => {
          error(
            t('person.errors.gettingAvailableAdminLabels', {
              defaultValue: `Error getting available admin labels: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
          throw err
        })
    },
    getPersonLabels: async () => {
      return getPersonLabels(currentPerson.id)
        .then((data) => {
          personLabelsActions.setAdminLabels(data.labels, data.changes)
        })
        .catch((err) => {
          error(`Error getting admin labels: ${err.message || ''}`, err)
          throw err
        })
    },
    addLabel: async () => {
      const label = {
        id: '',
        timeStamp: '',
        personId: currentPerson.id,
        valueText: personLabelsState.labelSuggestion.label.valueText,
        humanReadableText: personLabelsState.labelSuggestion.label.humanReadableValueText,
        cisId: user.cisId,
        reason: personLabelsState.addReason,
        oar: false,
        hco: false,
        locked: false,
        hidden: false,
        ownerRestricted: false,
        genealogicalDate: '',
      }

      return addAdminLabel(currentPerson.id, label)
        .then((response) => {
          personLabelsActions.setAdminLabels(response.data.labels, response.data.changes)
          personLabelsActions.reset()
        })
        .catch((err) => {
          const errorMessage =
            err?.response?.status === 503
              ? 'Person copy did not complete completely. Please check the person record to verify the action was performed correctly.'
              : err.message || ''

          error(
            t('person.errors.addingAdminLabel', {
              defaultValue: `Error adding admin label: ${err.message || ''}`,
              message: errorMessage,
            }),
            err
          )
          throw err
        })
    },
    editLabel: async (label) => {
      return editAdminLabel(label.id, personLabelsState.labelReason)
        .then(() => {
          personLabelsActions.getPersonLabels()
          personLabelsActions.getPersonLabelMetadata()
          personLabelsActions.reset()
        })
        .catch((err) => {
          error(
            t('person.errors.editingAdminLabel', {
              defaultValue: `Error editing admin label: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
          throw err
        })
    },
    deleteLabel: async (label) => {
      return deleteAdminLabel(currentPerson.id, label.id, personLabelsState.deleteReason)
        .then((data) => {
          personLabelsActions.setAdminLabels(data.labels, data.changes)
          personLabelsActions.reset()
        })
        .catch((err) => {
          error(
            t('person.errors.deletingAdminLabel', {
              defaultValue: `Error deleting admin label: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
          throw err
        })
    },
    updatePersonLabelsState: () => {
      setPersonLabelsState(personLabelsState)
    },
  }
  return { personLabelsState, personLabelsActions }
}
